import { Box, Button, Tab, Tabs, Typography } from '@mui/material';
import React, { FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DescriptionIcon from '@mui/icons-material/Description';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import FileAccessGrantService, {
  FileAccessGrantAdminListResponse,
  FileAccessGrantAdminViewDto,
  FileAccessGrantListFilters,
} from 'services/file-access-grant.service';
import { useSnackbar } from 'notistack';
import GrantAccessTable from './components/GrantAccessTable';
import GrantAccessModal from './components/GrantAccessModal';
import GrantDeleteModal from './components/GrantDeleteModal';

const service = new FileAccessGrantService();

const FileAccessGrant: FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const [grantModalOpen, setGrantModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedGrant, setSelectedGrant] = useState<FileAccessGrantAdminViewDto | null>(null);

  const [data, setData] = useState<FileAccessGrantAdminListResponse>({
    items: [],
    page: 1,
    limit: 10,
    total: 0,
  });

  const [filters, setFilters] = useState<FileAccessGrantListFilters>({
    personName: '',
    personEmail: '',
    carrierBranchTradeName: '',
    carrierBranchDocumentNumber: '',
  });

  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  const normalizeFilters = (filters: FileAccessGrantListFilters) => {
    const normalized: Record<string, unknown> = {};

    Object.entries(filters).forEach(([key, value]) => {
      if (value === '' || value === null || value === undefined) return;

      if (key === 'carrierBranchDocumentNumber' && typeof value === 'string') {
        const digits = value.replace(/\D/g, '');
        if (!digits) return;
        normalized[key] = digits;
        return;
      }

      normalized[key] = value;
    });

    return normalized;
  };

  const fetchData = async (page = 1, filterValues = filters) => {
    setLoading(true);
    try {
      const response = await service.listAdmin({
        ...normalizeFilters(filterValues),
        page,
        limit: data.limit,
      });

      setData(response as typeof data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(1, filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const handleFilterChange = (newFilters: typeof filters) => {
    setFilters(newFilters);
    fetchData(1, newFilters);
  };

  const handleDelete = async (id: number) => {
    const grant = data.items.find((item) => item.id === id);

    if (grant) {
      setSelectedGrant(grant);
      setDeleteModalOpen(true);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!selectedGrant) return;

    setLoading(true);
    try {
      await service.delete(selectedGrant.id);
      fetchData(data.page, filters);
    } finally {
      setLoading(false);
      setDeleteModalOpen(false);
      setSelectedGrant(null);
    }
  };

  const handlePageChange = (page: number) => {
    fetchData(page, filters);
  };

  const handleExport = async () => {
    setExportLoading(true);

    try {
      const blob = await service.exportAdminXlsx({
        ...normalizeFilters(filters),
        page: data.page,
        limit: data.limit,
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'acesso_gestao_documentacao.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      enqueueSnackbar('Erro ao exportar os dados. Contate o suporte.', { variant: 'error' });
    } finally {
      setExportLoading(false);
    }
  };

  const handleGrantAccess = async (payload: { userIds: number[]; companyId: number }) => {
    // eslint-disable-next-line no-restricted-syntax
    for (const userId of payload.userIds) {
      // eslint-disable-next-line no-await-in-loop
      await service.create({
        personId: userId,
        carrierBranchId: payload.companyId,
      });
    }

    fetchData(data.page, filters);
  };

  return (
    <>
      <Box px={6} pt={1}>
        <Tabs
          value={1}
          onChange={(_, v) => {
            if (v === 0) navigate('/documentacao/gerenciamento');
          }}
          sx={{
            borderBottom: '1px solid #E0E0E0',
            '& .MuiTab-root': { textTransform: 'none', fontSize: '14px', fontWeight: 500, minHeight: '64px' },
          }}
        >
          <Tab label="Gestão de documento" icon={<DescriptionIcon />} iconPosition="start" sx={{ minWidth: 220 }} />
          <Tab label="Acesso Documentação" icon={<AdminPanelSettingsIcon />} iconPosition="start" sx={{ minWidth: 220 }} />
        </Tabs>
      </Box>
      <Box p={4}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={4}>
          <Box pr={3}>
            <Typography fontSize={24} fontWeight={700} color="grey.800">
              Acesso Gestão de Documentação
            </Typography>

            <Typography fontSize={14} fontWeight={400} color="grey.700">
              Existem dados pessoais sensíveis na documentação a ser compartilhada. No caso de dúvida, verifique no Manual de Uso da
              Plataforma se o acesso pode ser concedido à pessoa que está solicitando.
            </Typography>
          </Box>

          <Button variant="contained" color="secondary" disableElevation sx={{ px: 10 }} onClick={() => setGrantModalOpen(true)}>
            Conceder Acesso
          </Button>
        </Box>

        <GrantAccessTable
          data={data}
          filters={filters}
          loading={loading}
          exportLoading={exportLoading}
          onPageChange={handlePageChange}
          onFilterChange={handleFilterChange}
          onDelete={handleDelete}
          onExport={handleExport}
        />
      </Box>

      <GrantAccessModal open={grantModalOpen} onClose={() => setGrantModalOpen(false)} onGrantAccess={handleGrantAccess} />

      <GrantDeleteModal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onDelete={handleDeleteConfirm}
        personName={selectedGrant?.personDetails.name || ''}
        companyName={selectedGrant?.carrierBranchDetails.companyName || ''}
      />
    </>
  );
};

export default FileAccessGrant;
